import { Link, useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import { CalendarDays, Map, MapPin, Copy, Trash2, Share2, DollarSign, Link2, Calendar, Globe } from 'lucide-react'
import { useTrip } from './useTrip'
import { useDeleteTrip, useDuplicateTrip } from './useTrips'
import { usePoints } from '../timeline/usePoints'
import { useTripPhase } from '../../shared/hooks/useTripPhase'
import { StatusPill } from '../../shared/components/StatusPill'
import { DayBadge } from '../../shared/components/DayBadge'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { formatDateRange, tripDayCount } from '../../shared/lib/dates'
import CalendarSyncPanel from '../calendar/CalendarSyncPanel'
import { OfflineIndicator } from '../sync/OfflineIndicator'
import { publishStory } from '../../shared/api/story'
import { TripEditPanel } from './TripEditPanel'
import { addDays } from '../../shared/lib/dates'

export function TripOverview() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: trip, isLoading, isError } = useTrip(id)
  const { data: points = [] } = usePoints(id)
  const duplicate = useDuplicateTrip()
  const remove = useDeleteTrip()
  const phase = useTripPhase(trip)
  const [showCalendar, setShowCalendar] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [expandedDay, setExpandedDay] = useState<number | null>(null)
  const [calToast, setCalToast] = useState<string | null>(null)
  const [_storySlug, setStorySlug] = useState<string | null>(
    trip?.slug ?? null,
  )
  const [storyToast, setStoryToast] = useState<string | null>(null)

  if (isLoading) return <Spinner label="Loading trip…" />
  if (isError || !trip) {
    return (
      <EmptyState
        title="Trip not found"
        description="It may have been deleted."
        action={
          <Link to="/">
            <Button variant="outline">Back to trips</Button>
          </Link>
        }
      />
    )
  }

  const days = tripDayCount(trip.start_date, trip.end_date)

  async function handleDuplicate() {
    const copy = await duplicate.mutateAsync(trip!.id)
    navigate(`/trips/${copy.id}`)
  }

  async function handleDelete() {
    if (!window.confirm(`Delete “${trip!.title}”? This can’t be undone.`)) return
    await remove.mutateAsync(trip!.id)
    navigate('/')
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="overflow-hidden rounded-st-lg border border-st-line bg-st-surface shadow-sm">
        <div
          className="relative h-40 w-full md:h-52"
          style={{ background: 'linear-gradient(135deg, #2D6A4F 0%, #81B29A 100%)' }}
        >
          <div className="absolute right-4 top-4">
            <StatusPill phase={phase} />
          </div>
        </div>
        <div className="p-5 md:p-6">
          <h1 className="font-display text-2xl font-bold text-st-text md:text-3xl">{trip.title}</h1>
          <Button variant="outline" onClick={() => setShowEdit(true)}>Edit trip</Button>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-st-muted">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-4 w-4" /> {formatDateRange(trip.start_date, trip.end_date)}
            </span>
            <OfflineIndicator tripId={id} />
            {trip.destination && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-4 w-4" /> {trip.destination}
              </span>
            )}
          </div>
          {trip.description && <p className="mt-3 text-sm text-st-text">{trip.description}</p>}

          <div className="mt-5 grid grid-cols-3 gap-3">
            <Stat label="Days" value={days} />
            <Stat label="Visits" value={points.length} />
            <Stat label="Photos" value={0} />
          </div>
        </div>
      </div>

      <section className="mt-6">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-st-muted">Days</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {Array.from({ length: days }).map((_, i) => {
            const dayPoints = points.filter((p) => p.day_index === i)
            const count = dayPoints.length
            return (
              <div key={i} className="rounded-st-md border border-st-line bg-st-surface p-3 text-sm">
                <div className="flex items-center gap-2"><Link to={`/trips/${trip.id}/timeline?day=${i}`} className="flex min-w-0 flex-1 items-center gap-2 hover:text-st-primary">
                  <DayBadge dayIndex={i} /><span>{trip.start_date ? new Intl.DateTimeFormat(undefined, { weekday:'short', year:'numeric', month:'long', day:'numeric', timeZone:'UTC' }).format(new Date(`${addDays(trip.start_date,i)}T12:00:00Z`)) : `Day ${i+1}`}</span></Link>
                  <button type="button" onClick={()=>setExpandedDay(expandedDay===i?null:i)} aria-expanded={expandedDay===i} className="text-st-primary">{count} places</button></div>
                {expandedDay===i && <ul className="mt-2 border-t border-st-line pt-2">{dayPoints.map(p=><li key={p.id}>{p.time ? `${p.time} · ` : ''}{p.title}</li>)}</ul>}
              </div>
            )
          })}
        </div>
      </section>

      <section className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link to={`/trips/${trip.id}/timeline`}>
          <Button variant="primary" className="w-full">
            <CalendarDays className="h-4 w-4" /> Open timeline
          </Button>
        </Link>
        <Link to={`/trips/${trip.id}/map`}>
          <Button variant="outline" className="w-full">
            <Map className="h-4 w-4" /> Open map
          </Button>
        </Link>
        <Link to={`/trips/${trip.id}/export`}>
          <Button variant="ghost" className="w-full">
            <Share2 className="h-4 w-4" /> Export
          </Button>
        </Link>
        <Link to={`/trips/${trip.id}/budget`}>
          <Button variant="ghost" className="w-full">
            <DollarSign className="h-4 w-4" /> Budget
          </Button>
        </Link>
        <Link to={`/trips/${trip.id}/links`}>
          <Button variant="ghost" className="w-full">
            <Link2 className="h-4 w-4" /> Links
          </Button>
        </Link>
        <button
          type="button"
          onClick={() => setShowCalendar(true)}
          className="flex w-full items-center justify-center gap-2 rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text hover:bg-st-surface-2 transition"
        >
          <Calendar className="h-4 w-4" /> Calendar Sync
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!id) return
            const res = await publishStory(id, !trip.is_public)
            setStorySlug(res.slug)
            if (res.is_public && res.slug) {
              const url = `${window.location.origin}/story/${res.slug}`
              await navigator.clipboard.writeText(url).catch(() => {})
              setStoryToast(`Story published! Link copied: /story/${res.slug}`)
            } else {
              setStoryToast('Story unpublished.')
            }
            setTimeout(() => setStoryToast(null), 5000)
          }}
          className="flex w-full items-center justify-center gap-2 rounded-st-md border border-st-line bg-st-surface px-3 py-2 text-sm text-st-text hover:bg-st-surface-2 transition"
        >
          <Globe className="h-4 w-4" />
          {trip.is_public ? 'Unpublish Story' : 'Share Story'}
        </button>
        <Button variant="ghost" className="w-full" onClick={handleDuplicate} disabled={duplicate.isPending}>
          <Copy className="h-4 w-4" /> {duplicate.isPending ? 'Duplicating…' : 'Use as template'}
        </Button>
      </section>

      <section className="mt-8 border-t border-st-line pt-5">
        <Button variant="danger" onClick={handleDelete} disabled={remove.isPending}>
          <Trash2 className="h-4 w-4" /> Delete trip
        </Button>
      </section>

      {showCalendar && id && (
        <CalendarSyncPanel
          tripId={id}
          pointIds={points.map((p) => p.id)}
          onClose={() => setShowCalendar(false)}
          onImported={(n) => {
            setCalToast(`Imported ${n} event${n !== 1 ? 's' : ''} as trip points`)
            setTimeout(() => setCalToast(null), 4000)
          }}
          onExported={(n) => {
            setCalToast(`Exported ${n} point${n !== 1 ? 's' : ''} to Google Calendar`)
            setTimeout(() => setCalToast(null), 4000)
          }}
        />
      )}
      {showEdit && <TripEditPanel trip={trip} onClose={() => setShowEdit(false)} />}
      {calToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm">
          {calToast}
        </div>
      )}
      {storyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-blue-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm max-w-sm text-center">
          {storyToast}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-st-md bg-st-surface-2 px-3 py-2 text-center">
      <p className="text-xl font-semibold text-st-text">{value}</p>
      <p className="text-xs text-st-muted">{label}</p>
    </div>
  )
}
